import { useContext, useMemo } from 'react';
import { PluginContext } from 'ts-components/Plugins/context';
import {
  AutomationStep,
  CalendarSource,
  DataAdornment,
  FeatureFlaggedArtifacts,
  FeatureFlags,
  FloatingFrame,
  ObjectSettingsMenuItem,
  PluginBlockEntry,
  ToolbarItem,
} from './types';

export const getFlagsForPlugin = (
  pluginApiName: string | undefined,
  featureFlagsMap: FeatureFlags | undefined
) => {
  const pluginFlags = pluginApiName
    ? featureFlagsMap?.[pluginApiName]
    : undefined;

  const flagProxy: FeatureFlags[string] = new Proxy(
    pluginFlags || ({} as any),
    {
      get(artifactMap, artifactType) {
        if (Reflect.has(artifactMap, artifactType)) {
          return new Proxy(Reflect.get(artifactMap, artifactType), {
            get(apiNameMap, artifactApiName) {
              if (Reflect.has(apiNameMap, artifactApiName)) {
                return Reflect.get(apiNameMap, artifactApiName);
              }

              // If the API name doesn't exist, return `true` since this
              // means there was no `when` clause (so no feature flag)
              return true;
            },
          });
        }

        // If the artifact type doesn't exist, return false for any API name
        return new Proxy(
          {},
          {
            get() {
              return false;
            },
          }
        );
      },
    }
  );

  return flagProxy;
};

export const filterFloatingFrames = (
  frames: FloatingFrame[],
  flags: FeatureFlags
) => {
  const result = frames.filter((f) => {
    const featureFlags = getFlagsForPlugin(f.plugin_api_name, flags);
    return featureFlags.floatingFrames[f.api_name];
  });

  return result;
};

export const filterDataAdornments = (
  adornments: DataAdornment[],
  flags: FeatureFlags
) => {
  const result = adornments.filter((a) => {
    const featureFlags = getFlagsForPlugin(a.plugin_api_name, flags);
    return featureFlags.dataAdornments[a.field_type];
  });

  return result;
};

export const filterAutomationSteps = (
  automationSteps: AutomationStep[],
  flags: FeatureFlags
): AutomationStep[] => {
  const mappedSteps = automationSteps.map((a) => {
    const featureFlags = getFlagsForPlugin(a.plugin_api_name, flags);

    const actions = structuredClone(a.actions);
    const actionKeys = Object.keys(actions);
    for (const key of actionKeys) {
      if (!featureFlags.automationActions[key]) {
        delete actions[key];
      }
    }

    if (!Object.keys(actions).length) {
      return null;
    }

    return {
      ...a,
      actions,
    };
  });

  const filteredSteps = mappedSteps.filter(Boolean) as AutomationStep[];

  return filteredSteps;
};

export const filterObjectSettingsMenuItems = (
  objectSettingsMenuItems: ObjectSettingsMenuItem[],
  flags: FeatureFlags
) => {
  const result = objectSettingsMenuItems.filter((o) => {
    const featureFlags = getFlagsForPlugin(o.plugin_api_name, flags);
    return featureFlags.objectSettings[o.api_name];
  });

  return result;
};

export const filterToolbarItems = (
  toolbarItems: ToolbarItem[],
  flags: FeatureFlags
) => {
  const result = toolbarItems.filter((t) => {
    const featureFlags = getFlagsForPlugin(t.plugin_api_name, flags);
    return featureFlags.toolbarItems[t.api_name];
  });

  return result;
};

export const filterCalendarSources = (
  calendarSources: CalendarSource[],
  flags: FeatureFlags
) => {
  const result = calendarSources.filter((c) => {
    const featureFlags = getFlagsForPlugin(c.plugin_api_name, flags);
    return featureFlags.calendarSources[c.api_name];
  });

  return result;
};

export const filterCustomBlocks = (
  pluginBlockEntries: PluginBlockEntry[],
  flags: FeatureFlags
): PluginBlockEntry[] => {
  return pluginBlockEntries.map((entry) => ({
    ...entry,
    blocks: entry.blocks.filter((block) => {
      const featureFlags = getFlagsForPlugin(entry.plugin_api_name, flags);
      return featureFlags.customBlocks[block.api_name];
    }),
  }));
};

export type FeatureFlagsForPlugin = Record<
  FeatureFlaggedArtifacts,
  Record<string, boolean>
>;

export const usePluginFeatureFlags = (
  pluginApiName?: string
): FeatureFlagsForPlugin => {
  const { featureFlags } = useContext(PluginContext);

  const flagState: FeatureFlags[string] = useMemo(() => {
    return getFlagsForPlugin(pluginApiName, featureFlags);
  }, [featureFlags, pluginApiName]);

  return flagState;
};
